import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import inventoryUsageService from '../../services/InventoryUsageService.js';
import { FileText, Pencil, ArrowLeft } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { formatUsernameLocation } from '../../utils/userDisplay';


const ViewInventoryUsage = () => {
  const { t } = useTranslation();
  const { usageId } = useParams();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsageRecord();
  }, [usageId]);

  const fetchUsageRecord = async () => {
    try {
      const response = await inventoryUsageService.getById(usageId);
      setUsage(response.data);
    } catch (err) {
      Alert.error('Failed to load usage record');
      console.error(err);
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  const getReceiptUrl = () => {
    if (usage?.return_receipt_base64) {
      return `data:image/jpeg;base64,${usage.return_receipt_base64}`;
    }
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <div className="max-w-[1850px] mx-auto">
        <Header />
        <div className="flex">
          <Sidebar />
          <div className="flex bg-light w-full mt-16 sm:mt-0">
            <div className="flex-1 flex flex-col overflow-hidden">
              <main className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                  <p className="text-sm sm:text-base text-gray-600">Loading usage record...</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!usage) {
    return null;
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <FileText className="w-5 h-5 text-buttonBg" />Usage Record Details
                  </h1>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  {/* Header Section */}
                  <div className="bg-primary p-3 px-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4">
                      <div className="flex-1 min-w-0">
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-secondary mb-1 break-words">
                          {usage.item_name}
                        </h2>
                        <p className="text-sm text-gray-600">Usage ID: {usage.usage_id}</p>
                      </div>
                      <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button
                          onClick={() => navigate(`/inventory-usage/edit/${usageId}`)}
                          className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit Record
                        </button>
                        <button
                          onClick={() => navigate(`/inventory/view/${usage.inventory_id}`)}
                          className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Back to Item
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    {/* Basic Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Usage ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{usage.usage_id}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Inventory ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{usage.inventory_id}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Item Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{usage.item_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Usage Date</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(usage.usage_date)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Usage Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Usage Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Usage Type</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                              {usage.usage_type}
                            </span>
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Usage Purpose</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                              {usage.usage_purpose}
                            </span>
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Quantity Used</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{usage.quantity_used} {usage.unit}</p>
                        </div>
                      </div>
                    </div>

                    {/* Cost Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Cost Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Unit Price</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatCurrency(usage.unit_price)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Total Cost</label>
                          <p className="text-sm sm:text-base text-green-600 font-bold break-all">{formatCurrency(usage.total_cost)}</p>
                        </div>
                      </div>
                    </div>

                    {/* User Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        User Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Used By Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{usage.used_by_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Name / Location</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            {usage.used_by_display
                              || formatUsernameLocation(
                                usage.used_by_name || usage.used_by_username,
                                usage.used_by_location,
                                usage.used_by_name || usage.used_by_user_id || 'N/A'
                              )}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Department</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{usage.department || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Vehicle</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{usage.vehicle_id || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    {usage.description && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Description
                        </h3>
                        <p className="text-sm sm:text-base text-secondary break-all whitespace-pre-wrap">{usage.description}</p>
                      </div>
                    )}

                    {/* Return Receipt */}
                    {usage.return_receipt_base64 && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Return Receipt / Proof
                        </h3>
                        <button
                          onClick={() => setShowReceiptModal(true)}
                          className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonhoverTxt hover:bg-buttonhoverBg border rounded-sm transition-all duration-300 font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Receipt
                        </button>
                      </div>
                    )}

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">{t('common.createdBy')}</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{usage.created_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(usage.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Updated By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{usage.updated_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(usage.updated_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Document Modal */}
      <DocumentModal
        isOpen={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        documentUrl={getReceiptUrl()}
        documentType="Return Receipt"
      />
    </div>
  );
};

export default ViewInventoryUsage;